import type { MaskedDataFieldType } from "./types";

/**
 * Format a raw SSN string (digits only) into XXX-XX-XXXX
 */
export function formatSSN(value: string): string {
	const digits = stripNonNumeric(value).slice(0, 9);
	if (digits.length <= 3) return digits;
	if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
	return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
}

/**
 * Format a raw numeric string into currency: $1,234.56
 */
export function formatCurrency(value: string): string {
	const cleaned = stripNonCurrency(value);
	const parts = cleaned.split(".");
	const whole = parts[0] || "0";
	const decimal = parts[1] === undefined ? "" : `.${parts[1].slice(0, 2)}`;
	const formatted = whole.replaceAll(/\B(?=(\d{3})+(?!\d))/g, ",");
	return `$${formatted}${decimal}`;
}

/**
 * Format a raw numeric string with thousands separators: 1,234
 */
export function formatNumber(value: string): string {
	const cleaned = value.replaceAll(/[^\d.-]/g, "");
	const negative = cleaned.startsWith("-");
	const withoutSign = negative ? cleaned.slice(1) : cleaned;
	const parts = withoutSign.split(".");
	const whole = parts[0] || "0";
	const decimal = parts[1] === undefined ? "" : `.${parts[1]}`;
	const formatted = whole.replaceAll(/\B(?=(\d{3})+(?!\d))/g, ",");
	return `${negative ? "-" : ""}${formatted}${decimal}`;
}

/**
 * Return a masked string using the given pattern
 */
export function maskValue(maskPattern: string = "•••••"): string {
	return maskPattern;
}

/**
 * Strip all non-numeric characters from a string
 */
export function stripNonNumeric(value: string): string {
	return value.replaceAll(/\D/g, "");
}

/**
 * Strip all non-numeric characters except decimal point
 */
export function stripNonCurrency(value: string): string {
	const cleaned = value.replaceAll(/[^\d.]/g, "");
	// Only allow one decimal point
	const parts = cleaned.split(".");
	if (parts.length <= 2) return cleaned;
	return `${parts[0]}.${parts.slice(1).join("")}`;
}

/**
 * Format a raw value based on its type for display
 */
export function formatForDisplay(
	value: string,
	type: MaskedDataFieldType,
): string {
	if (!value) return "";
	switch (type) {
		case "ssn":
			return formatSSN(value);
		case "currency":
			return formatCurrency(value);
		case "number":
			return formatNumber(value);
		case "text":
			return value;
		default:
			return value;
	}
}

/**
 * Sanitize raw input based on type (restrict allowed characters)
 */
export function sanitizeInput(
	value: string,
	type: MaskedDataFieldType,
): string {
	switch (type) {
		case "ssn":
			return stripNonNumeric(value).slice(0, 9);
		case "currency":
			return stripNonCurrency(value);
		case "number": {
			const cleaned = value.replaceAll(/[^\d.-]/g, "");
			const isNeg = cleaned.startsWith("-");
			const digits = cleaned.replaceAll(/[^\d.]/g, "");
			return isNeg ? `-${digits}` : digits;
		}
		case "text":
			return value;
		default:
			return value;
	}
}
